/**
 * Utility to clear all caches and force fresh content on every load
 * This ensures users always see the latest content without any stale data
 */

const CACHE_VERSION = 'v4';

export const clearAllCaches = async (): Promise<void> => {
  if (!('caches' in window)) return;

  try {
    const cacheNames = await caches.keys();
    
    if (cacheNames.length > 0) {
      console.log(`🧹 Clearing all ${cacheNames.length} cache(s) for fresh content`);
      await Promise.all(cacheNames.map(name => caches.delete(name)));
      console.log('✅ All caches cleared successfully');
    }
  } catch (error) {
    console.error('Error clearing caches:', error);
  }
};

export const clearOldCaches = async (): Promise<void> => {
  if (!('caches' in window)) return;

  try {
    const cacheNames = await caches.keys();
    
    // Delete any cache that doesn't include our current version
    const cachesToDelete = cacheNames.filter(name => {
      // Keep workbox precache (managed by workbox)
      if (name.includes('workbox-precache')) return false;
      // Delete if it doesn't include our current version
      if (!name.includes(CACHE_VERSION)) return true;
      return false;
    });

    if (cachesToDelete.length > 0) {
      console.log(`🧹 Clearing ${cachesToDelete.length} old cache(s):`, cachesToDelete);
      await Promise.all(cachesToDelete.map(name => caches.delete(name)));
      console.log('✅ Old caches cleared successfully');
    }
  } catch (error) {
    console.error('Error clearing old caches:', error);
  }
};

export const forceRefreshServiceWorker = async (): Promise<void> => {
  if (!('serviceWorker' in navigator)) return;

  try {
    const registration = await navigator.serviceWorker.getRegistration();
    if (registration) {
      // Force update check
      await registration.update();
      
      // If there's a waiting worker, activate it immediately
      if (registration.waiting) {
        registration.waiting.postMessage({ type: 'SKIP_WAITING' });
        console.log('🔄 New service worker activated');
      }
    }
  } catch (error) {
    console.error('Error updating service worker:', error);
  }
};

export const clearImageCache = async (): Promise<void> => {
  if (!('caches' in window)) return;

  try {
    const cacheNames = await caches.keys();
    const imageCaches = cacheNames.filter(name => name.includes('images'));
    
    await Promise.all(imageCaches.map(name => caches.delete(name)));
    console.log('🖼️ Image caches cleared');
  } catch (error) {
    console.error('Error clearing image caches:', error);
  }
};

export const clearBrowserStorage = (): void => {
  try {
    // Clear sessionStorage (temporary session data)
    sessionStorage.clear();
    console.log('📦 Session storage cleared');
  } catch (error) {
    console.error('Error clearing session storage:', error);
  }
};

// Initialize aggressive cache clearing on app load
export const initCacheClearing = (): void => {
  // Clear ALL caches to ensure completely fresh content
  clearAllCaches();
  
  // Force service worker update
  forceRefreshServiceWorker();
  
  // Clear session storage for fresh state
  clearBrowserStorage();
  
  // Set up periodic cache check (every 2 minutes while app is open)
  setInterval(() => {
    forceRefreshServiceWorker();
    clearOldCaches();
  }, 2 * 60 * 1000);
};
